/**
 * PublicNavbar - Reusable navigation bar for all public-facing pages.
 *
 * Centralizes the nav link list so adding a new page only requires
 * one entry in NAV_LINKS below.
 *
 * Usage:
 *   <PublicNavbar currentPage="/pricing" />
 */

import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Menu,
  X,
  LayoutDashboard,
  Settings,
  LogOut,
  Sun,
  Moon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { UserMenu } from './UserMenu';

// -------------------------------------------------------------------
// Central nav link registry. Add new public pages here once.
// -------------------------------------------------------------------
const NAV_LINKS: { path: string; label: string }[] = [
  { path: '/', label: 'Home' },
  { path: '/benchmark', label: 'Benchmark' },
  { path: '/docs', label: 'Docs' },
  { path: '/pricing', label: 'Pricing' },
];

interface PublicNavbarProps {
  /** Path of the current page, used to highlight the active link.
   *  Falls back to useLocation().pathname when omitted. */
  currentPage?: string;
}

export function PublicNavbar({ currentPage }: PublicNavbarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { resolvedTheme, setMode } = useTheme();

  const activePath = currentPage ?? location.pathname;

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    const currentPath = location.pathname;
    logout();
    if (currentPath === '/preferences') {
      navigate('/dashboard');
    } else {
      navigate(currentPath);
    }
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border-subtle">
      <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-12">
        <div className="flex items-center justify-between h-14 sm:h-16">
          {/* Logo */}
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          >
            <img
              src="/octoverdict-no-bg.png"
              alt="Vettis"
              className="w-5 h-5 sm:w-6 sm:h-6 object-contain"
            />
            <span className="text-base sm:text-lg font-semibold tracking-tight">
              <span className="text-verdict-primary">Vettis</span>
            </span>
          </button>

          {/* Desktop Center Nav Links */}
          <div className="hidden sm:flex items-center gap-8 text-sm text-text-secondary">
            {NAV_LINKS.map((link) => (
              <button
                key={link.path}
                onClick={() => navigate(link.path)}
                className={cn(
                  'transition-colors',
                  activePath === link.path
                    ? 'text-text-primary'
                    : 'hover:text-text-primary'
                )}
              >
                {link.label}
              </button>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="sm:hidden p-2 rounded-lg hover:bg-surface-light transition-colors"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <X className="w-5 h-5 text-text-secondary" />
            ) : (
              <Menu className="w-5 h-5 text-text-secondary" />
            )}
          </button>

          {/* Desktop Auth Buttons */}
          <div className="hidden sm:flex items-center gap-3">
            <ThemeToggle resolvedTheme={resolvedTheme} setMode={setMode} />
            {user ? (
              <>
                <button
                  onClick={() => navigate('/dashboard')}
                  className="px-4 py-1.5 text-sm border border-border hover:bg-surface-light transition-colors rounded-lg"
                >
                  Console
                </button>
                <UserMenu
                  user={user}
                  onLogout={handleLogout}
                  showConsoleLink
                />
              </>
            ) : (
              <LoggedOutButtons navigate={navigate} />
            )}
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="sm:hidden py-4 border-t border-border-subtle animate-fade-in">
            <div className="flex flex-col gap-2">
              <div className="px-4 pb-2 flex items-center justify-between">
                <span className="text-xs text-text-muted uppercase tracking-wider">Theme</span>
                <ThemeToggle resolvedTheme={resolvedTheme} setMode={setMode} />
              </div>
              {NAV_LINKS.map((link) => (
                <button
                  key={link.path}
                  onClick={() => { navigate(link.path); setMobileMenuOpen(false); }}
                  className={cn(
                    'w-full px-4 py-2.5 text-sm transition-colors rounded-lg text-left',
                    activePath === link.path
                      ? 'text-text-primary hover:bg-surface-light'
                      : 'text-text-secondary hover:text-text-primary hover:bg-surface-light'
                  )}
                >
                  {link.label}
                </button>
              ))}
              {user ? (
                <>
                  <button
                    onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-text-secondary hover:text-text-primary hover:bg-surface-light transition-colors rounded-lg"
                  >
                    <LayoutDashboard className="w-4 h-4" />
                    Console
                  </button>
                  <button
                    onClick={() => { navigate('/preferences'); setMobileMenuOpen(false); }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-text-secondary hover:text-text-primary hover:bg-surface-light transition-colors rounded-lg"
                  >
                    <Settings className="w-4 h-4" />
                    Settings
                  </button>
                  <div className="border-t border-border-subtle mt-2 pt-2">
                    <button
                      onClick={() => { setMobileMenuOpen(false); handleLogout(); }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-error hover:text-error/80 hover:bg-error/10 transition-colors rounded-lg"
                    >
                      <LogOut className="w-4 h-4" />
                      Log out
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <button
                    onClick={() => { navigate('/login'); setMobileMenuOpen(false); }}
                    className="w-full px-4 py-2.5 text-sm text-text-secondary hover:text-text-primary hover:bg-surface-light transition-colors rounded-lg text-left"
                  >
                    Log In
                  </button>
                  <button
                    onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }}
                    className="w-full px-4 py-2.5 text-sm bg-verdict-primary/10 border border-verdict-primary/30 text-verdict-primary hover:bg-verdict-primary/20 transition-colors rounded-lg text-left"
                  >
                    Try it now
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}


/* ------------------------------------------------------------------ */
/*  Internal sub-components                                            */
/* ------------------------------------------------------------------ */

function ThemeToggle({ resolvedTheme, setMode }: { resolvedTheme: 'light' | 'dark'; setMode: (mode: 'light' | 'dark') => void }) {
  return (
    <button
      onClick={() => setMode(resolvedTheme === 'dark' ? 'light' : 'dark')}
      className="p-2 rounded-lg hover:bg-surface-light transition-colors text-text-secondary hover:text-text-primary"
      aria-label={`Switch to ${resolvedTheme === 'dark' ? 'light' : 'dark'} mode`}
    >
      {resolvedTheme === 'dark' ? (
        <Sun className="w-4 h-4" />
      ) : (
        <Moon className="w-4 h-4" />
      )}
    </button>
  );
}

function LoggedOutButtons({ navigate }: { navigate: (path: string) => void }) {
  return (
    <>
      <button
        onClick={() => navigate('/login')}
        className="px-4 py-1.5 text-sm text-text-secondary hover:text-text-primary transition-colors"
      >
        Log In
      </button>
      <button
        onClick={() => navigate('/dashboard')}
        className="px-4 py-1.5 text-sm bg-verdict-primary/10 border border-verdict-primary/30 text-verdict-primary hover:bg-verdict-primary/20 transition-colors rounded-lg"
      >
        Try it now
      </button>
    </>
  );
}
